/** Marble row -> Elevate v1 adapter. Presentation and authority stay separate. */
import { compareElevateItems, type ElevateItem } from '@solum/experience'
import type { RecordHistoryEntry, Row } from '@solum/sdk'

export type TimelineEntry = {
  key: string
  kind: 'activity' | 'audit' | 'work' | 'meeting' | 'lane'
  at: string
  row: Row
}

export interface TimelineElevateGroup {
  id: string
  label?: string
  personId?: string
  items: ElevateItem[]
}

const text = (value: unknown, fallback = '') => value == null ? fallback : String(value)
const label = (value: unknown, fallback: string) => {
  const raw = text(value, fallback).replaceAll('_', ' ')
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : fallback
}
const revision = (row: Row) => text(row.updated_at ?? row.at ?? row.occurred_at ?? row.due_at ?? row.id)
const source = (row: Row, id: string, sourceLabel: string) => ({ id, label: sourceLabel, kind: 'marble' as const, revision: revision(row) })
const open = (sourceId: string) => ({
  id: 'open',
  label: 'Open',
  sourceId,
  state: 'available' as const,
  batch: 'single' as const,
})

/**
 * The activity door's ordering expression for accountable work, kept as one
 * adapter seam so the drawer's pre-grouping and the Elevate item cannot drift.
 * A due date wins when present; creation time positions valid undated work.
 */
export const workEffectiveAt = (row: Row): string => text(row.due_at ?? row.created_at)

export function elevateTimelineItem(entry: Exclude<TimelineEntry, { kind: 'lane' }>, ownerLabel: (id: unknown) => string): ElevateItem {
  const row = entry.row
  if (entry.kind === 'activity') {
    const itemSource = source(row, 'marble:activity', 'Marble Activity')
    const id = text(row.id)
    const action = open(itemSource.id)
    return {
      contractVersion: '1.0.0',
      id: `activity:${id}`,
      source: itemSource,
      provenance: {
        entity: 'activity_activity', id,
        destination: { state: 'available', deepLink: `/e/activity_activity/${id}` },
      },
      kind: 'activity',
      channel: text(row.channel, 'activity'),
      title: text(row.subject, 'Untitled activity'),
      /* The door's own body, with its own redaction flag.
         `activity.contextual_timeline` projects `body` and `body_redacted`
         together and IS the redaction boundary: a row visible only through a
         linked person comes back as `[redacted]`. This adapter used to discard
         both and manufacture `"<Direction> activity"` in their place — a
         sentence nobody wrote, standing where the governed content should be,
         while the direction it was built from was never shown as a direction.
         Direction is a badge now, so the summary line is free for the body. */
      body: row.body == null ? undefined : {
        text: text(row.body),
        redacted: row.body_redacted === true,
      },
      direction: row.direction ? text(row.direction) : undefined,
      time: {
        /* ★ The DOOR'S ordering expression, preferred over re-deriving it.
           activity 0.13.0 returns `effective_at` = coalesce(occurred_at,
           due_at, created_at) precisely so a client sorts on the answer it was
           given. Recomputing `occurred_at ?? due_at` here dropped the
           created_at fallback, so an UNDATED activity got '' — and
           compareElevateItems string-sorts, which sank every undated row to the
           bottom of the list while the door had placed it by its creation time.
           That is #1996's defect surviving in the client after the door was
           fixed. The fallbacks remain for the other sources, which have no
           effective_at of their own. */
        effectiveAt: text(row.effective_at ?? row.occurred_at ?? row.due_at ?? row.created_at),
        occurredAt: row.occurred_at ? text(row.occurred_at) : undefined,
        dueAt: row.due_at ? text(row.due_at) : undefined,
      },
      actors: row.owner_id ? [{ id: text(row.owner_id), label: ownerLabel(row.owner_id), role: 'owner', kind: 'person' }] : [],
      related: row.person_id ? [{ entity: 'core_person', id: text(row.person_id), label: text(row.person_label, 'Linked person') }] : [],
      status: { id: text(row.status, 'unknown'), label: label(row.status, 'Unknown') },
      priority: row.priority ? { id: text(row.priority), label: label(row.priority, 'Normal') } : undefined,
      actions: [action],
    }
  }

  if (entry.kind === 'work') {
    const itemSource = source(row, 'marble:work', 'Marble Work')
    const id = text(row.id)
    const entity = text(row.entity_type)
    const action = open(itemSource.id)
    return {
      contractVersion: '1.0.0',
      id: `work:${entity}:${id}`,
      source: itemSource,
      provenance: { entity, id, destination: { state: 'available', deepLink: `/e/${entity}/${id}` } },
      kind: 'work',
      // A task remains a Task in the visible Elevate vocabulary even though
      // Marble correctly stores accountable work separately from activities.
      // Other module-owned work keeps the broader Work item category.
      channel: entity === 'core_task' ? 'task' : 'work',
      title: text(row.title, 'Untitled work'),
      summary: `Assigned work from ${label(row.module, 'an installed module')}.`,
      time: {
        // The contextual-work door orders undated work by creation time. Carry
        // that same governed fallback into Elevate: an optional due date is not
        // the item's only possible deterministic position on the timeline.
        effectiveAt: workEffectiveAt(row),
        dueAt: row.due_at ? text(row.due_at) : undefined,
      },
      actors: row.assignee_id ? [{ id: text(row.assignee_id), label: 'You', role: 'assignee', kind: 'person' }] : [],
      related: [],
      status: { id: text(row.status, 'unknown'), label: label(row.status, 'Unknown') },
      priority: row.priority != null ? { id: text(row.priority), label: Number(row.priority) === 1 ? 'High' : `Priority ${text(row.priority)}` } : undefined,
      actions: [action],
    }
  }

  if (entry.kind === 'meeting') {
    const itemSource = source(row, 'marble:meetings', 'Marble Meetings')
    const id = text(row.id)
    const action = open(itemSource.id)
    const provider = row.source_system ? ` via ${text(row.source_system)}` : ''
    return {
      contractVersion: '1.0.0',
      id: `meeting:${id}`,
      source: itemSource,
      provenance: {
        entity: 'engagements_engagement', id,
        destination: { state: 'available', deepLink: `/e/engagements_engagement/${id}` },
      },
      kind: 'meeting',
      channel: 'meeting',
      title: text(row.title, 'Untitled meeting report'),
      summary: row.description ? text(row.description) : `Meeting report${provider}`,
      time: {
        effectiveAt: text(row.scheduled_at ?? row.created_at),
        occurredAt: row.created_at ? text(row.created_at) : undefined,
        startsAt: row.scheduled_at ? text(row.scheduled_at) : undefined,
      },
      actors: row.owner_id ? [{ id: text(row.owner_id), label: ownerLabel(row.owner_id), role: 'owner', kind: 'person' }] : [],
      related: [],
      status: { id: text(row.status, 'unknown'), label: label(row.status, 'Unknown') },
      actions: [action],
    }
  }

  /* An audit row is a `core.record_history` entry, already mapped by the SDK's
     allowlist (#2444 R2): labels, words and the server-derived detail. There is
     no payload and no principal id to read here — the projection returns
     neither — so nothing on this card can be the browser's own account of what
     happened or a machine key standing in for a name. */
  const change = row as Partial<RecordHistoryEntry>
  const itemSource = source(row, 'marble:audit', 'Marble Changes')
  const id = text(change.seq ?? change.at)
  const via = change.viaSourceLabel ? `via ${change.viaSourceLabel}` : null
  const summary = [change.detail, via].filter((part): part is string => Boolean(part)).join(' · ')
  const actor = change.actorLabel
    ? (change.onBehalfOfLabel ? `${change.actorLabel} on behalf of ${change.onBehalfOfLabel}` : change.actorLabel)
    : null
  return {
    contractVersion: '1.0.0',
    id: `audit:${id}`,
    source: itemSource,
    provenance: {
      entity: 'core_audit', id,
      destination: { state: 'refused', reason: 'Audit evidence is read in this timeline.' },
    },
    kind: 'audit',
    title: text(change.actionLabel, 'Recorded change'),
    summary: summary || undefined,
    time: { effectiveAt: text(change.at), occurredAt: change.at ? text(change.at) : undefined },
    actors: actor ? [{ id: `audit-actor:${id}`, label: actor, role: 'actor', kind: 'person' }] : [],
    related: [],
    status: { id: 'recorded', label: 'Recorded' },
    actions: [],
  }
}

export function elevateTimelineGroups(entries: readonly TimelineEntry[], ownerLabel: (id: unknown) => string): TimelineElevateGroup[] {
  const groups: TimelineElevateGroup[] = []
  let current: TimelineElevateGroup = { id: 'timeline', items: [] }
  for (const entry of entries) {
    if (entry.kind === 'lane') {
      if (current.items.length > 0) groups.push(current)
      current = {
        id: `lane-${text(entry.row.person_id, 'record')}`,
        label: text(entry.row.label, 'This record'),
        personId: entry.row.person_id ? text(entry.row.person_id) : undefined,
        items: [],
      }
    } else {
      current.items.push(elevateTimelineItem(entry, ownerLabel))
    }
  }
  if (current.items.length > 0) groups.push(current)
  /* The contract's comparator, not the caller's string sort, decides order:
   * two rows stamped the same second must not swap on a re-read because the
   * host array arrived differently. Host array order is never meaning. */
  for (const group of groups) group.items.sort(compareElevateItems)
  return groups
}
